import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface SpaceForm {
  name: string;
  type: string;
  description?: string;
}

interface Props {
  onComplete: () => void;
}

const StartWithSpace: React.FC<Props> = ({ onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SpaceForm>();

  const onSubmit = async (data: SpaceForm) => {
    setLoading(true);
    setError('');

    try {
      await api.post('/onboarding/start-with-space', data);
      toast.success('🏞️ Your space has been defined!');
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create space');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 4 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h2" sx={{ fontSize: '3rem', mb: 2 }}>
          🏞️
        </Typography>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Define Your Space
        </Typography>
        <Typography variant="body1" color="text.secondary">
          A space is a growing area that can contain multiple gardens
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          label="Space Name"
          margin="normal"
          {...register('name', {
            required: 'Please name your space',
          })}
          error={!!errors.name}
          helperText={errors.name?.message || 'e.g., "Backyard", "Front Garden", "Rooftop"'}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Space Type</InputLabel>
          <Select
            defaultValue="backyard"
            {...register('type', { required: 'Please select a type' })}
            error={!!errors.type}
          >
            <MenuItem value="backyard">Backyard</MenuItem>
            <MenuItem value="frontyard">Front Yard</MenuItem>
            <MenuItem value="rooftop">Rooftop</MenuItem>
            <MenuItem value="greenhouse">Greenhouse</MenuItem>
            <MenuItem value="balcony">Balcony</MenuItem>
            <MenuItem value="patio">Patio</MenuItem>
            <MenuItem value="community">Community Garden</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Description (Optional)"
          margin="normal"
          multiline
          rows={3}
          {...register('description')}
          helperText="Describe your space - size, features, sun exposure, etc."
        />

        <Box mt={4}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Create My Space'}
          </Button>
        </Box>
      </form>

      <Box mt={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          💡 Tip: You can create multiple gardens within your space to organize different plant types!
        </Typography>
      </Box>
    </Paper>
  );
};

export default StartWithSpace;